import {useMemo} from 'react'
import {useLocation} from 'react-router-dom'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import hrefLang from '../components/seo/href-lang'

/**
 * Creates a clean canonical URL by removing query parameters while preserving filtered navigation
 * @param {string} url - The full URL including query parameters
 * @returns {string} - The clean canonical URL without query parameters
 */
const createCleanCanonicalUrl = (url) => {
    try {
        const urlObj = new URL(url)
        return `${urlObj.origin}${urlObj.pathname}`
    } catch (error) {
        return url
    }
}

/**
 * Creates a clean hreflang and URL
 * @param {string} url - The full URL including query parameters
 * @param {string} pageType - The full URL including query parameters
 * @param {object} config - The config object contains supported locales
 * @returns {object} - The clean hreflang object
 */
const createHrefLangArray = (url, pageType, supportedLocales) => {
    try {
        const baseUrl = new URL(url)

        return supportedLocales.map((locale) => {
            if (!locale?.alias) {
                return {
                    localeId: locale?.id || '',
                    href: url
                }
            }

            const localeUrl = new URL(baseUrl)
            const pathParts = baseUrl.pathname.split('/').filter(Boolean)

            // Remove any existing locale segments to prevent duplicates
            const cleanParts = pathParts.filter(
                (part) => !supportedLocales.some((l) => l.alias && part === l.alias.split('/')[0])
            )

            if (pageType === 'home') {
                localeUrl.pathname = `/${locale.alias}/home`
            } else {
                localeUrl.pathname = `/${locale.alias}/${cleanParts.join('/')}`
            }

            return {
                localeId: locale.id,
                href: localeUrl.toString()
            }
        })
    } catch (error) {
        logger.error('use seo', {
            namespace: 'seo.createHrefLangArray',
            additionalProperties: {error: error.message}
        })
        return []
    }
}

/**
 * Creates a clean hreflang and URL for PDP.
 * PDP should have only en_ca
 * @param {string} url - The full URL including query parameters
 * @param {object} config - The config object contains supported locales
 * @returns {object} - The clean hreflang object
 */
const createHrefLangPDPArray = (url, supportedLocales) => {
    try {
        const isCaLocale = supportedLocales.find((locale) => locale?.id === 'en-ca')

        return isCaLocale ? [{localeId: isCaLocale.id, href: url}] : []
    } catch (error) {
        logger.error('use seo', {
            namespace: 'seo.createHrefLangPDPArray',
            additionalProperties: {error: error.message}
        })
        return []
    }
}

/**
 * Generates SEO meta tag data for PWA Kit pages.
 * @param {Object} params
 * @param {string} params.pageType - 'home' | 'product' | 'category' | 'page'
 * @param {Object} params.data - Data for the page
 * @returns {Object} Object containing seoData
 */
export default function useSeo({pageType, data}) {
    const {site} = useMultiSite()
    const location = useLocation()
    const supportedLocales = hrefLang?.supportedLocales

    return useMemo(() => {
        const currentUrl = typeof window !== 'undefined' ? window.location.href : null
        if (!currentUrl) {
            return {seoData: null}
        }

        const seoData = {
            canonicalUrl: createCleanCanonicalUrl(currentUrl),
            hrefLang: []
        }

        const getMetaValue = (id) => data?.find((item) => item.id === id)?.value || ''

        switch (pageType) {
            case 'Home':
                seoData.hrefLang = createHrefLangArray(currentUrl, 'home', supportedLocales)
                break

            case 'Products':
                if (data) {
                    seoData.title = getMetaValue('title')
                    seoData.description = getMetaValue('description')
                    seoData.keywords = getMetaValue('keywords')
                }
                seoData.hrefLang = createHrefLangPDPArray(currentUrl, supportedLocales)
                break

            case 'Collections':
                if (data) {
                    seoData.title = getMetaValue('title')
                    seoData.description = getMetaValue('description')
                    seoData.keywords = getMetaValue('keywords')
                }
                seoData.hrefLang = createHrefLangArray(currentUrl, 'category', supportedLocales)
                break
        }

        return {seoData}
    }, [pageType, data, site, location.pathname])
}
